import React from "react";
import { Button } from "../../../components/forms/Buttons/Button";
import { CHIP_TONES } from "../../../components/QuestionList/FilterChips";
import { quietButtonClass } from "../../Exams/TestBuilder/ui";

// The look shared by the booklet's PDF and key sections (tokens of the test builder and the list pages).
export const dialogFooterClass =
  "-mx-6 -mb-6 mt-6 flex flex-col-reverse gap-2 rounded-b-2xl border-t border-slate-200 bg-slate-50/60 px-6 py-4 sm:flex-row sm:justify-end";
// Buttons are 44px high on phones and take the whole row there.
export const phoneButtonClass = "max-sm:h-11 max-sm:w-full";
export const sectionHeaderClass =
  "flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-start sm:justify-between sm:p-5";
export const sectionTitleClass = "flex items-center gap-2 text-base font-semibold text-slate-900";
export const sectionTextClass = "mt-1 max-w-4xl text-sm text-slate-500";

// The shared Button's spinner, in place of the icon (at the icon's size).
function ButtonSpinner({ className = "h-5 w-5" }) {
  return (
    <svg className={`${className} shrink-0 animate-spin`} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" className="opacity-30" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

/**
 * A Button whose request is under way (`busy`): a spinner in place of its icon, its clicks ignored (aria-disabled,
 * aria-busy). It keeps the keyboard focus, its name and its width, where the shared Button's `loading` disables it:
 * that drops the focus to the page and leaves a nameless spinner.
 */
export function BusyButton({ busy, Icon, text, onClick, className = "", ...rest }) {
  return (
    <Button
      {...rest}
      Icon={busy ? undefined : Icon}
      aria-disabled={busy || undefined}
      aria-busy={busy || undefined}
      onClick={(event) => {
        if (!busy) onClick?.(event);
      }}
      className={`aria-disabled:cursor-wait aria-disabled:opacity-50 aria-disabled:shadow-none ${className}`}
    >
      {busy && <ButtonSpinner />}
      {text}
    </Button>
  );
}

/**
 * A secondary action beside a section's or a document's main one: its icon and text, no frame — the test bank's quiet
 * button (quietButtonClass: 36px high, 44px on phones, a 16px icon), as the page's other quiet actions are. `busy` as
 * BusyButton's: a spinner in place of the icon, the clicks ignored, the focus and the name kept.
 */
export function QuietButton({ busy = false, Icon, text, onClick, className = "", ...rest }) {
  return (
    <button
      type="button"
      {...rest}
      aria-disabled={busy || undefined}
      aria-busy={busy || undefined}
      onClick={(event) => {
        if (!busy) onClick?.(event);
      }}
      className={`${quietButtonClass} aria-disabled:cursor-wait aria-disabled:opacity-60 ${className}`}
    >
      {busy ? <ButtonSpinner className="h-4 w-4" /> : Icon && <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />}
      {text}
    </button>
  );
}

// A small label: preview, out of date, confidential.
export function Badge({ tone = "slate", children, Icon }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
        (CHIP_TONES[tone] || CHIP_TONES.slate).badge
      }`}
    >
      {Icon && <Icon className="h-3.5 w-3.5" aria-hidden="true" />}
      {children}
    </span>
  );
}

// A boxed message inside a section: info (blue), warning (amber), error (red), success (green) or muted.
const NOTICE_TONES = {
  info: "border-blue-200 bg-blue-50/60",
  warning: "border-amber-200 bg-amber-50",
  error: "border-red-200 bg-red-50",
  success: "border-emerald-200 bg-emerald-50",
  muted: "border-slate-200 bg-slate-50",
};
const NOTICE_ICONS = {
  info: "text-blue-600",
  warning: "text-amber-600",
  error: "text-red-600",
  success: "text-emerald-600",
  muted: "text-slate-500",
};

export function Notice({ tone = "info", Icon, title, children, actions, role }) {
  return (
    <div role={role} className={`rounded-xl border p-4 ${NOTICE_TONES[tone] || NOTICE_TONES.info}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          {Icon && <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${NOTICE_ICONS[tone] || NOTICE_ICONS.info}`} aria-hidden="true" />}
          <div className="min-w-0 flex-1 text-sm text-slate-700">
            {title && <p className="font-semibold text-slate-900">{title}</p>}
            {children}
          </div>
        </div>
        {actions && <div className="flex shrink-0 flex-col gap-2 sm:flex-row">{actions}</div>}
      </div>
    </div>
  );
}
